import JSZip from "jszip";
import jsPDF from "jspdf";
import {saveAs} from "file-saver";
import {templateCertificates} from "../../../../common/define";

const fontCache = new Map();
const imageCache = new Map();

export class CertificateModel {
    constructor(name, date, type, record) {
        this.name = name ?? '';
        this.date = date ?? '';
        this.type = type;
        this.data = record
    }
}

export const generateCertificates = async (records) => {
    const zip = new JSZip();
    const usedFileNames = new Set();
    const generatedRecords = [];

    for (const student of records) {
        const isGenerated = await generateCertificate(student, zip, usedFileNames)
        if (isGenerated) {
            generatedRecords.push(student)
        }
    }

    if (!generatedRecords.length) {
        return [];
    }

    const zipBlob = await zip.generateAsync({type: "blob"});
    saveAs(zipBlob, `Certificates.zip`);
    return generatedRecords;
}

export const generateCertificate = async (certificate, zip, usedFileNames = new Set()) => {
    const typeTemplate = templateCertificates.find(template => template.id === certificate.type)
    if (!typeTemplate) return false;
    const templateName = typeTemplate.fontName;
    const nameCertificate = certificate.name
    const fontFile = typeTemplate.fontFile
    const pdf = new jsPDF({
        orientation: typeTemplate.type,
        unit: "px",
        format: [typeTemplate.height, typeTemplate.width]
    });
    const FlamingoFont = await getFont(fontFile)
    const url = require(`@/assets/img/${typeTemplate.image}`)
    const image = await loadImage(url);
    pdf.addImage(image, getImageFormat(typeTemplate.image), 0, 0, typeTemplate.width, typeTemplate.height);
    pdf.addFileToVFS(fontFile, FlamingoFont);
    pdf.addFont(fontFile, templateName, "normal");
    pdf.setFont(templateName);
    const pageWidth = pdf.internal.pageSize.width;
    pdf.setFontSize(typeTemplate.fontSizeName);
    const text = await capitalizeText(nameCertificate)
    pdf.text(text, pageWidth / 2, typeTemplate.topName, {align: "center"});
    pdf.setFontSize(typeTemplate.fontSizeDate);
    pdf.setTextColor(typeTemplate.color === 2 ? 255 : 0, typeTemplate.color === 2 ? 255 : 0, typeTemplate.color === 2 ? 255 : 0);
    pdf.text(certificate.date, typeTemplate.leftDate, typeTemplate.topDate);
    const pdfData = pdf.output("arraybuffer");
    if (!pdfData?.byteLength) {
        throw new Error("Tạo file chứng chỉ thất bại !");
    }
    const uniqueFileName = buildUniqueCertificateFileName({
        displayName: text,
        templateName: typeTemplate.templateName,
        userId: certificate?.data?.user_id,
        usedFileNames
    });
    zip.file(uniqueFileName, pdfData);
    return true;
}

const buildUniqueCertificateFileName = ({displayName, templateName, userId, usedFileNames}) => {
    const safeDisplayName = sanitizeFileName(displayName || "Certificate");
    const safeTemplateName = sanitizeFileName(templateName || "Template");
    const userSuffix = userId ? `-${sanitizeFileName(String(userId))}` : "";
    const baseFileName = `${safeDisplayName}-${safeTemplateName}${userSuffix}`;
    let candidate = `${baseFileName}.pdf`;
    let index = 1;

    while (usedFileNames.has(candidate)) {
        candidate = `${baseFileName}-${index}.pdf`;
        index += 1;
    }

    usedFileNames.add(candidate);
    return candidate;
}

const sanitizeFileName = (value = "") => {
    return value.replace(/[<>:"/\\|?*\x00-\x1F]/g, "").trim() || "Certificate";
}

export const getFont = async (fontFile) => {
    if (fontCache.has(fontFile)) {
        return fontCache.get(fontFile);
    }

    const response = await fetch(`/fonts/${fontFile}`);
    if (!response.ok) {
        throw new Error("Không tải được font chứng chỉ !");
    }
    const fontData = await response.arrayBuffer();
    const base64Font = arrayBufferToBase64(fontData);
    fontCache.set(fontFile, base64Font);
    return base64Font;
}

export const loadImage = async (url) => {
    if (imageCache.has(url)) {
        return imageCache.get(url);
    }

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error("Không tải được ảnh chứng chỉ !");
    }
    const imageData = new Uint8Array(await response.arrayBuffer());
    imageCache.set(url, imageData);
    return imageData;
}

const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";

    for (let index = 0; index < bytes.length; index += chunkSize) {
        const chunk = bytes.subarray(index, index + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
    }

    return btoa(binary);
}

const getImageFormat = (imageName = "") => {
    return imageName.toLowerCase().endsWith(".png") ? "PNG" : "JPEG";
}

export const capitalizeText = async (text) => {
    if (!text) return null
    return text
        .toLowerCase()
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}